import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useReveal } from '@/hooks/useReveal';
import { fetchReviews, mediaUrl } from '@/lib/api';

function StarRating({ rating, size = 'sm' }) {
    const sizeClass = size === 'lg' ? 'w-5 h-5' : size === 'md' ? 'w-4 h-4' : 'w-3.5 h-3.5';
    return (
        <div className="flex items-center gap-0.5">
            {[1, 2, 3, 4, 5].map((star) => (
                <svg
                    key={star}
                    className={`${sizeClass} ${star <= rating ? 'text-primary' : 'text-muted-foreground/30'}`}
                    fill="currentColor"
                    viewBox="0 0 20 20"
                >
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
            ))}
        </div>
    );
}

function HeroBanner() {
    return (
        <section className="relative w-full pt-16 lg:pt-20 overflow-hidden bg-secondary border-b border-border/40">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
                <div className="text-center">
                    <p className="section-label mb-4">From the Stage</p>
                    <h1 className="font-display text-section-title font-semibold text-foreground leading-tight mb-6">
                        Artist Reviews &amp; Recommendations
                    </h1>
                    <p className="text-muted-foreground text-base lg:text-lg max-w-2xl mx-auto leading-relaxed">
                        Celebrated Sri Lankan artists share their experience performing with Awakasha Entertainment across Australia.
                    </p>
                </div>
            </div>
            <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent" />
        </section>
    );
}

function ArtistReviewCard({ artist, index }) {
    const { ref, revealed } = useReveal();
    const isEven = index % 2 === 0;

    return (
        <div
            ref={ref}
            className={`reveal-up ${revealed ? 'revealed' : ''} stagger-${(index % 4) + 1}`}
        >
            <div className="bg-white border border-border/60 rounded-2xl overflow-hidden hover:border-primary/30 transition-all duration-300 hover:shadow-lg">
                <div className={`grid grid-cols-1 lg:grid-cols-5 gap-0 ${isEven ? '' : 'lg:flex-row-reverse'}`}>
                    <div className={`lg:col-span-2 relative ${isEven ? 'lg:order-1' : 'lg:order-2'}`}>
                        <div className="aspect-square lg:aspect-auto lg:h-full min-h-[280px] relative overflow-hidden">
                            <img
                                src={mediaUrl(artist.artistImage)}
                                alt={artist.artistImageAlt || `${artist.artistName} portrait`}
                                className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                                loading="lazy"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent lg:bg-gradient-to-r lg:from-transparent lg:to-black/20" />
                            <div className="absolute top-4 left-4">
                                <span className="bg-primary text-foreground text-xs font-bold px-3 py-1.5 rounded-full tracking-wide uppercase">
                                    {artist.genre}
                                </span>
                            </div>
                            <div className="absolute bottom-4 left-4 lg:hidden">
                                <p className="text-white font-display font-bold text-2xl">{artist.artistName}</p>
                                <p className="text-white/80 text-sm">{artist.artistTitle}</p>
                            </div>
                        </div>
                    </div>

                    <div className={`lg:col-span-3 p-6 lg:p-8 flex flex-col gap-5 ${isEven ? 'lg:order-2' : 'lg:order-1'}`}>
                        <div className="hidden lg:block">
                            <div className="flex items-start justify-between gap-4">
                                <div>
                                    <h3 className="font-display text-xl font-semibold text-foreground">{artist.artistName}</h3>
                                    <p className="text-muted-foreground text-sm mt-0.5">{artist.artistTitle}</p>
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                    <StarRating rating={artist.rating} size="md" />
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center gap-4 flex-wrap">
                            <div className="flex items-center gap-1.5 bg-primary/10 border border-primary/20 rounded-full px-3 py-1.5">
                                <svg className="w-3.5 h-3.5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                                </svg>
                                <span className="text-primary text-xs font-semibold">{artist.eventsPerformed} Events with Awakasha</span>
                            </div>
                            {artist.verified ? (
                                <div className="flex items-center gap-1.5">
                                    <svg className="w-3.5 h-3.5 text-primary" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                    <span className="text-xs text-muted-foreground">Verified Artist</span>
                                </div>
                            ) : null}
                        </div>

                        <div className="relative">
                            <div className="absolute -top-1 -left-1 text-primary/20">
                                <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                                </svg>
                            </div>
                            <blockquote className="pl-6 border-l-2 border-primary/30">
                                <p className="text-foreground font-medium text-base leading-relaxed italic">
                                    &ldquo;{artist.quote}&rdquo;
                                </p>
                            </blockquote>
                        </div>

                        <div className="bg-secondary rounded-xl p-4 border border-border/40">
                            <div className="flex items-center gap-2 mb-2">
                                <svg className="w-4 h-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                                </svg>
                                <span className="text-xs font-bold text-primary uppercase tracking-wider">Full Recommendation</span>
                            </div>
                            <p className="text-muted-foreground text-sm leading-relaxed">{artist.recommendation}</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function ArtistCTA() {
    const { ref, revealed } = useReveal();
    return (
        <div
            ref={ref}
            className={`reveal-up ${revealed ? 'revealed' : ''} mt-12 text-center`}
        >
            <div className="bg-white border border-border/60 rounded-2xl p-8 lg:p-12">
                <div className="w-14 h-14 rounded-full bg-primary/15 border border-primary/30 flex items-center justify-center mx-auto mb-5">
                    <svg className="w-7 h-7 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                    </svg>
                </div>
                <h3 className="font-display text-2xl font-semibold text-foreground mb-3">
                    Are You a Sri Lankan Artist?
                </h3>
                <p className="text-muted-foreground text-base max-w-xl mx-auto leading-relaxed mb-6">
                    Join the growing family of celebrated artists who have performed with Awakasha Entertainment. Showcase your talent to the Sri Lankan community across Australia.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                    <Link
                        to="/contact"
                        className="btn-gold flex items-center gap-2 px-7 py-3.5 rounded-full text-sm"
                    >
                        <span>Get in Touch</span>
                        <svg className="w-4 h-4 relative z-10" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                        </svg>
                    </Link>
                    <Link
                        to="/events"
                        className="btn-outline-gold flex items-center gap-2 px-7 py-3.5 rounded-full text-sm"
                    >
                        <span>View Upcoming Events</span>
                    </Link>
                </div>
            </div>
        </div>
    );
}

function ArtistReviewsSection({ reviews }) {
    const { ref: headRef, revealed: headRevealed } = useReveal();

    return (
        <section
            id="artist-reviews"
            className="bg-secondary py-16 lg:py-20 border-t border-border/40 scroll-mt-24"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div ref={headRef} className={`reveal-up ${headRevealed ? 'revealed' : ''} mb-12 lg:mb-16`}>
                    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                        <div>
                            <p className="section-label mb-3">From the Stage</p>
                            <h2 className="font-display text-section-title font-semibold text-foreground leading-tight gold-underline">
                                Artist Reviews &amp; Recommendations
                            </h2>
                        </div>
                        <p className="text-muted-foreground text-sm max-w-xs text-right hidden sm:block">
                            Celebrated Sri Lankan artists share their experience performing with Awakasha Entertainment.
                        </p>
                    </div>
                </div>

                {reviews.length === 0 ? (
                    <p className="text-center text-muted-foreground py-12">Artist reviews coming soon.</p>
                ) : (
                    <div className="flex flex-col gap-8">
                        {reviews.map((artist, index) => (
                            <ArtistReviewCard key={artist.id} artist={artist} index={index} />
                        ))}
                    </div>
                )}

                <ArtistCTA />
            </div>
        </section>
    );
}

export default function ReviewsPageContent() {
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        fetchReviews()
            .then((data) => {
                if (!cancelled) setReviews(data);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    return (
        <>
            <HeroBanner />
            {loading ? (
                <section className="bg-secondary py-16 lg:py-20 border-t border-border/40">
                    <p className="text-center text-muted-foreground">Loading reviews...</p>
                </section>
            ) : (
                <ArtistReviewsSection reviews={reviews} />
            )}
        </>
    );
}
